import { useCallback, useEffect, useState, useRef } from 'react'
import {
  Room,
  RoomEvent,
  ConnectionState,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client'

import { useLiveKitToken, useLiveKitRoomDelete } from '@/hooks/useLiveKit'
import { CallUI } from '@/features/callUI/CallUI'
import type { CallState } from '@/features/callUI/types'

const TRANSCRIPT_TOPIC = 'livekit.transcript'
const AGENT_CONNECT_TIMEOUT = 15000

let currentRoomName: string | null = null

interface LiveKitCallButtonProps {
  campaignId: number
  agentId: string
  disabled?: boolean
}

let globalActiveCall: { campaignId: number; agentId: string } | null = null
const globalCallListeners = new Set<() => void>()

function notifyGlobalCallChange() {
  globalCallListeners.forEach((listener) => listener())
}

export function LiveKitCallButton({ campaignId, agentId, disabled }: LiveKitCallButtonProps) {
  const tokenMutation = useLiveKitToken()
  const roomDeleteMutation = useLiveKitRoomDelete()

  const [callState, setCallState] = useState<CallState>('idle')
  const [room, setRoom] = useState<Room | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [audioLevel, setAudioLevel] = useState(0)
  const [agentConnected, setAgentConnected] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [, forceUpdate] = useState({})
  const audioLevelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const agentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const roomRef = useRef<Room | null>(null)

  const [transcriptEvents, setTranscriptEvents] = useState<unknown[]>([])
  const [showEndedPanel, setShowEndedPanel] = useState(false)

  useEffect(() => {
    const listener = () => forceUpdate({})
    globalCallListeners.add(listener)
    return () => { globalCallListeners.delete(listener) }
  }, [])

  const isThisCallActive = globalActiveCall?.campaignId === campaignId && globalActiveCall?.agentId === agentId
  const isAnotherCallActive = globalActiveCall !== null && !isThisCallActive

  useEffect(() => {
    roomRef.current = room
  }, [room])

  const connectToRoom = useCallback(async (token: string, serverUrl: string) => {
    const newRoom = new Room({ adaptiveStream: true, dynacast: true })

    try {
      newRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        if (state === ConnectionState.Connected) {
          setCallState('connected')
          setConnectionError(null)
        } else if (state === ConnectionState.Disconnected) {
          setCallState('idle')
          globalActiveCall = null
          notifyGlobalCallChange()
        }
      })

      newRoom.on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _publication: RemoteTrackPublication, participant: RemoteParticipant) => {
          if (track.kind === Track.Kind.Audio) {
            const audioElement = track.attach()
            audioElement.id = `audio-${participant.identity}`
            document.body.appendChild(audioElement)
          }
        }
      )

      newRoom.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach().forEach((element) => element.remove())
      })

      newRoom.on(RoomEvent.Disconnected, () => {
        if (audioLevelIntervalRef.current) {
          clearInterval(audioLevelIntervalRef.current)
          audioLevelIntervalRef.current = null
        }
        if (agentTimeoutRef.current) {
          clearTimeout(agentTimeoutRef.current)
          agentTimeoutRef.current = null
        }
        setRoom(null)
        setIsMuted(false)
        setAudioLevel(0)
        setAgentConnected(false)
        currentRoomName = null
        document.querySelectorAll("audio[id^='audio-']").forEach((el) => el.remove())
        setCallState('ended')
        setStatusMessage('Call ended - you can review the transcript')
        setShowEndedPanel(true)
        globalActiveCall = null
        notifyGlobalCallChange()
      })

      newRoom.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        if (participant.identity.includes('agent') || participant.isAgent) {
          setAgentConnected(true)
          setStatusMessage('Agent connected - you can start talking')
          if (agentTimeoutRef.current) {
            clearTimeout(agentTimeoutRef.current)
            agentTimeoutRef.current = null
          }
        }
      })

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        if (participant.identity.includes('agent') || participant.isAgent) {
          setAgentConnected(false)
          setStatusMessage('Agent disconnected')
        }
      })

      newRoom.on(RoomEvent.DataReceived, (payload: Uint8Array, _participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
        if (topic !== TRANSCRIPT_TOPIC) return
        try {
          const text = new TextDecoder().decode(payload)
          const message = JSON.parse(text) as { type: string; event?: unknown }
          if (message.type === 'event' && message.event) {
            setTranscriptEvents((prev) => [...prev, message.event])
          }
        } catch (e) {
          console.error('Failed to parse transcript message:', e)
        }
      })

      await newRoom.connect(serverUrl, token)
      setRoom(newRoom)
      await newRoom.localParticipant.setMicrophoneEnabled(true)
      setStatusMessage('Connected to room, waiting for agent...')

      const existingAgents = Array.from(newRoom.remoteParticipants.values()).filter(
        (p) => p.identity.includes('agent') || p.isAgent
      )
      if (existingAgents.length > 0) {
        setAgentConnected(true)
        setStatusMessage('Agent connected - you can start talking')
      } else {
        agentTimeoutRef.current = setTimeout(() => {
          setConnectionError('Agent did not connect within 15 seconds. Please check that the LiveKit agent service is running.')
        }, AGENT_CONNECT_TIMEOUT)
      }

      if (audioLevelIntervalRef.current) clearInterval(audioLevelIntervalRef.current)
      audioLevelIntervalRef.current = setInterval(() => {
        const localTrack = newRoom.localParticipant.getTrackPublication(Track.Source.Microphone)
        if (localTrack?.track && !localTrack.isMuted) {
          setAudioLevel(Math.random() * 0.5 + 0.3)
        } else {
          setAudioLevel(0)
        }
      }, 100)
    } catch (error) {
      console.error('Error connecting to room:', error)
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect')
      setCallState('idle')
      setRoom(null)
      globalActiveCall = null
      currentRoomName = null
      notifyGlobalCallChange()
      try { await newRoom.disconnect() } catch { /* best-effort */ }
    }
  }, [])

  const startCall = useCallback(async () => {
    if (!campaignId || callState !== 'idle') return

    setCallState('connecting')
    setConnectionError(null)
    setTranscriptEvents([])
    setShowEndedPanel(false)
    globalActiveCall = { campaignId, agentId }
    notifyGlobalCallChange()

    try {
      const data = await tokenMutation.mutateAsync({ campaignId, agentId })
      if (data.token && data.serverUrl && data.roomName) {
        currentRoomName = data.roomName
        await connectToRoom(data.token, data.serverUrl)
      }
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : 'Failed to get token')
      setCallState('idle')
      globalActiveCall = null
      currentRoomName = null
      notifyGlobalCallChange()
    }
  }, [campaignId, agentId, callState, tokenMutation, connectToRoom])

  const endCall = useCallback(async () => {
    if (!room) return
    setCallState('disconnecting')

    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current)
      audioLevelIntervalRef.current = null
    }
    if (agentTimeoutRef.current) {
      clearTimeout(agentTimeoutRef.current)
      agentTimeoutRef.current = null
    }

    const roomNameToDelete = currentRoomName
    if (roomNameToDelete) {
      try { await roomDeleteMutation.mutateAsync(roomNameToDelete) } catch { /* best-effort */ }
    }

    try { await room.disconnect() } catch { /* best-effort */ }
    setRoom(null)
    setCallState('idle')
    setIsMuted(false)
    setAudioLevel(0)
    setAgentConnected(false)
    setStatusMessage(null)
    globalActiveCall = null
    currentRoomName = null
    notifyGlobalCallChange()
    document.querySelectorAll("audio[id^='audio-']").forEach((el) => el.remove())
  }, [room, roomDeleteMutation])

  const toggleMute = useCallback(async () => {
    if (!room) return
    const newMutedState = !isMuted
    await room.localParticipant.setMicrophoneEnabled(!newMutedState)
    setIsMuted(newMutedState)
  }, [room, isMuted])

  const closePanel = useCallback(() => {
    setShowEndedPanel(false)
    setCallState('idle')
    setStatusMessage(null)
    setTranscriptEvents([])
  }, [])

  useEffect(() => {
    return () => {
      if (audioLevelIntervalRef.current) clearInterval(audioLevelIntervalRef.current)
      if (agentTimeoutRef.current) clearTimeout(agentTimeoutRef.current)
      const currentRoom = roomRef.current
      if (currentRoom) {
        const roomNameToDelete = currentRoomName
        if (roomNameToDelete) {
          const apiBase = import.meta.env.VITE_API_URL || '/api/v1'
          fetch(`${apiBase}/livekit/room`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomName: roomNameToDelete }),
          }).catch(() => {})
        }
        currentRoom.disconnect()
        document.querySelectorAll("audio[id^='audio-']").forEach((el) => el.remove())
        globalActiveCall = null
        currentRoomName = null
        notifyGlobalCallChange()
      }
    }
  }, [])

  return (
    <CallUI
      variant="compact"
      callState={isThisCallActive ? callState : (callState === 'ended' && showEndedPanel ? 'ended' : callState)}
      isMuted={isMuted}
      connectionError={connectionError}
      statusMessage={statusMessage}
      audioLevel={audioLevel}
      agentConnected={agentConnected}
      transcriptEvents={transcriptEvents}
      startCall={startCall}
      endCall={endCall}
      toggleMute={toggleMute}
      closePanel={closePanel}
      disabled={disabled || isAnotherCallActive}
      showEndedPanel={showEndedPanel}
    />
  )
}
